import { useState, useCallback, FormEvent } from 'react';
import LegendLayer from "@arcgis/core/rest/support/LegendLayer";
import PrintParameters from "@arcgis/core/rest/support/PrintParameters";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import * as printTask from "@arcgis/core/rest/print.js"
import { getView } from '../map';
import { PrintMapControls, TempalteScaleBarUnit, TemplateFormat, TemplateLayout } from './types';



const useTitle = () => {
  const [title, setTitle] = useState<string>("")
  const changeTitle = useCallback((e: FormEvent<HTMLInputElement>) => {
    setTitle(e.currentTarget.value)
  }, [])
  return [title, changeTitle] as const
}

const useAuthor = () => {
  const [author, setAuthor] = useState<string>("")
  const changeAuthor = useCallback((e: FormEvent<HTMLInputElement>) => {
    setAuthor(e.currentTarget.value)
  }, [])
  return [author, changeAuthor] as const
}

const useCopyrightText = () => {
  const [text, setText] = useState<string>("")
  const changeText = useCallback((e: FormEvent<HTMLInputElement>) => {
    setText(e.currentTarget.value)
  }, [])
  return [text, changeText] as const
}

const useLayout = () => {
  const [layout, setLayout] = useState<TemplateLayout>("map-only")
  const changeLayout = useCallback((e: FormEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value
    switch (value) {
      case "map-only":
      case "a3-landscape":
      case "a3-portrait":
      case "a4-landscape":
      case "a4-portrait":
      case "letter-ansi-a-landscape":
      case "letter-ansi-a-portrait":
      case "tabloid-ansi-b-landscape":
      case "tabloid-ansi-b-portrait":
        setLayout(value)
        break;
      default:
        setLayout("map-only")
    }
  }, [])
  return [layout, changeLayout] as const
}

const useFormat = () => {
  const [format, setFormat] = useState<TemplateFormat>("pdf")
  const changeFormat = useCallback((e: FormEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value
    switch (value) {
      case "pdf":
      case "png32":
      case "png8":
      case "jpg":
      case "gif":
      case "eps":
      case "svg":
      case "svgz":
        setFormat(value)
        break;
      default:
        setFormat("pdf")
    }
  }, [])
  return [format, changeFormat] as const
}

const useScaleBarUnit = () => {
  const [unit, setUnit] = useState<TempalteScaleBarUnit>("Miles")
  const changeUnit = useCallback((e: FormEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value
    switch (value) {
      case "Miles":
      case "Kilometers":
      case "Meters":
      case "Feet":
        setUnit(value)
        break;
      default:
        setUnit("Miles")
    }
  }, [])
  return [unit, changeUnit] as const
}

export const usePrintTool = (): PrintMapControls => {
  const [title, setTitle] = useTitle()
  const [author, setAuthor] = useAuthor()
  const [copyrightText, setCopyrightText] = useCopyrightText()
  const [format, setFormat] = useFormat()
  const [layout, setLayout] = useLayout()
  const [scaleBarUnit, setScaleBarUnit] = useScaleBarUnit()
  const [printInProgress, setPrintInProgress] = useState<boolean>(false)

  const printMap = useCallback(() => {
    if (printInProgress) return
    const view = getView()
    if (view === null) return
    setPrintInProgress(true)
    const layersToPrint = new LegendLayer()
    layersToPrint.layerId = "Horry County"
    layersToPrint.subLayerIds = ['14', '15', '16', '17', '18', '19', '21', '24', '28', '29', '30', '31', '32']
    const printTemplate = new PrintTemplate()
    printTemplate.exportOptions = {
      width: 750,
      height: 1000,
      dpi: 80,
    }
    printTemplate.layoutOptions = {
      copyrightText,
      scalebarUnit: scaleBarUnit,
      titleText: title,
      authorText: author,
      legendLayers: [layersToPrint],
    }

    printTemplate.format = format
    printTemplate.layout = layout
    printTemplate.scalePreserved = false

    const printParams = new PrintParameters();
    printParams.view = view;
    printParams.template = printTemplate;

    printTask.execute('https://www.horrycountysc.gov/parcelapp/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task', printParams).then(result => {
      setPrintInProgress(false)
      window.open(result.url, "_blank")
      window.focus()
    })

  }, [printInProgress, title, author, format, layout, scaleBarUnit, copyrightText])

  return {
    title,
    author,
    copyrightText,
    format,
    layout,
    scaleBarUnit,
    printInProgress,
    actions: {
      setTitle,
      setAuthor,
      setCopyrightText,
      setLayout,
      setFormat,
      setScaleBarUnit,
      printMap,
    }
  }
}